import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/24/outline";
import { joinClassNames } from "@utils/helpers";
import { Card, Heading, Text } from "@components/theming";
import { useTranslations } from "next-intl";
import { useIsShopsite } from "@context/DomainContext";
import DialogCloseButton from "@components/popovers/dialogCloseButton";

interface Option {
  value: string | number;
  label: string | number | React.ReactElement;
}

interface Props {
  description?: string;
  /**
   * The rendered element that will act as a trigger for the dropdown
   * @param selected
   * @returns
   */
  children: (selected: string) => React.ReactNode;
  defaultValue?: string;
  /**
   * Handle the selection of options
   * @param option
   * @returns
   */
  onOptionSelect: (option: string) => void;
  /**
   * Options for the dropdown. It can either be an array of strings or numbers
   */
  options: Option[];
  onOpenChange?: (open: boolean) => void;
}

/**
 * This component should only be called only on mobile view, replacing dropdowns to better suit for mobile viewing
 *
 */
export default function MobileSelect({
  description = "",
  children,
  defaultValue = "",
  onOptionSelect,
  options,
  onOpenChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue);
  const t = useTranslations("Utils");
  const isShopsite = useIsShopsite();

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (onOpenChange) {
          onOpenChange(open);
        }
      }}
    >
      <DialogTrigger asChild>{children(selected)}</DialogTrigger>
      <AnimatePresence>
        {open && (
          <DialogContent
            asChild
            forceMount
            handleClose={() => {
              setOpen(false);
              if (onOpenChange) {
                onOpenChange(false);
              }
            }}
            shopsiteClasses="!text-indigo-800"
          >
            <Card asChild>
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: "100%",
                    opacity: 0,
                    backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                    backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
                  },
                }}
              >
                <div
                  className={joinClassNames(
                    "flex flex-col",
                    isShopsite ? "gap-4" : ""
                  )}
                >
                  <div
                    className={joinClassNames(
                      "flex flex-col pb-4",
                      isShopsite ? "px-6 pt-6 gap-3 bg-indigo-500" : "gap-4"
                    )}
                  >
                    <div className="relative self-end">
                      <DialogClose asChild>
                        <DialogCloseButton shopsiteVariant="white" />
                      </DialogClose>
                    </div>
                    <DialogHeader className="!text-left">
                      <DialogTitle asChild>
                        <Heading
                          as="h3"
                          size="5"
                          weight="bold"
                          shopsiteClasses="text-white text-xl"
                        >
                          {t("selectanoption")}
                        </Heading>
                      </DialogTitle>
                      {description ? (
                        <DialogDescription>
                          <Text color="gray" shopsiteClasses="text-white/75">
                            {description}
                          </Text>
                        </DialogDescription>
                      ) : (
                        <></>
                      )}
                      <CheckCircleIcon
                        className={joinClassNames(
                          "absolute top-0 left-4 w-24 h-24 -rotate-12",
                          isShopsite
                            ? "text-white/10"
                            : "text-[color:var(--accent-a3)]"
                        )}
                      />
                    </DialogHeader>
                  </div>
                  <div
                    className={joinClassNames(
                      "max-h-[calc(65dvh-116px)] overflow-y-auto",
                      isShopsite ? "pb-2" : ""
                    )}
                  >
                    {options.map((option) => (
                      <button
                        onClick={() => {
                          setSelected(option.value.toString());
                          onOptionSelect(option.value.toString());
                          setOpen(false);
                        }}
                        key={option.value}
                        className="flex flex-col items-center w-full"
                      >
                        <div className="relative flex items-center">
                          {option.value.toString() === selected ? (
                            <CheckIcon
                              className={joinClassNames(
                                "absolute right-[calc(100%-1rem)] top-2 stroke-[3] w-7 h-7",
                                isShopsite
                                  ? "text-indigo-500"
                                  : "text-[color:var(--accent-11)]"
                              )}
                            />
                          ) : (
                            <></>
                          )}
                          <Text
                            size="6"
                            weight="bold"
                            sharedClasses="py-2 px-6 w-fit"
                            whitelabelClasses={
                              option.value.toString() === selected
                                ? "text-[color:var(--accent-11)]"
                                : ""
                            }
                            shopsiteClasses={joinClassNames(
                              option.value.toString() === selected
                                ? "text-indigo-500"
                                : "text-black",
                              "text-2xl font-black"
                            )}
                          >
                            {option.label}
                          </Text>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
